/**
 * rebuy-atc-feedback.js — For Rowan (live theme, Pipeline 5.0.2)
 * SOLUTIONS-143
 * ---------------------------------------------------------------------------
 * WHY: this theme gives the shopper NOTHING when they click Add to Cart.
 *
 *   1. The theme's own confirmation popdown is disabled by one CSS rule —
 *      `assets/dev-custom.css:1` → `.product-add-popdown.is-visible{display:none}`
 *      (loaded at layout/theme.liquid:46). `addSuccess` builds that drawer and
 *      adds `.is-visible`; the rule kills exactly that state.
 *   2. Smart Cart then takes ~1s (warm, measured) to appear, because the theme
 *      adds via jQuery XHR — invisible to Rebuy's fetch wrapper — so Rebuy only
 *      notices via the theme's follow-up `fetch('/cart.js')`.
 *
 *   Net effect: click → silence → shopper clicks again.
 *
 * WHAT: an in-button loading state that starts on the click and persists until
 * the Smart Cart is genuinely VISIBLE, plus a re-entry guard so a second click
 * cannot fire a second add. Bounded by a hard ceiling so the button can never
 * get stuck.
 *
 * MEASURED FACTS THIS RELIES ON (2026-07-30, Playwright, 4x CPU):
 *   - `rebuy:smartcart.show` fires ~90-120ms BEFORE the flyout actually paints,
 *     so that event alone is NOT a safe clear signal. We prefer the
 *     `rebuy-cart-visible` body class, which lands after the show event.
 *   - Exactly ONE `/cart/add.js` per click, via XHR, from the theme. Rebuy does
 *     not add. So we must never block the first click.
 *
 * Uses ONLY documented Rebuy events: rebuy:smartcart.show, rebuy:cart.change.
 * Safe when Rebuy is absent — the ceiling restores the button and nothing else
 * changes, i.e. current live behaviour is preserved.
 */
(function () {
  'use strict';

  var CFG = window.RebuyATCConfig || {};
  if (CFG.enabled === false) return; // kill switch

  var ATC_SELECTOR = CFG.selector ||
    '[data-add-to-cart], .btn--add-to-cart, form[action*="/cart/add"] button[name="add"]';
  var FORM_SELECTOR = 'form[action*="/cart/add"]';

  var LOADING_CLASS = 'rebuy-atc--loading';
  var ADDED_CLASS = 'rebuy-atc--added';

  // Smart Cart's visible-state body classes, confirmed live on this store.
  var VISIBLE_CLASSES = CFG.visibleBodyClasses || ['rebuy-cart-visible', 'rebuy-modal-visible'];

  var HARD_CEILING_MS = CFG.hardCeilingMs || 6000; // absolute: button can never stick
  var CHANGE_GRACE_MS = CFG.changeGraceMs || 1200; // cart changed but never revealed
  var MIN_VISIBLE_MS = CFG.minVisibleMs || 250;    // no 40ms spinner flash on fast adds
  var ADDED_HOLD_MS = CFG.addedHoldMs || 2000;

  var activeBtn = null;   // the one add currently in flight
  var submitPass = null;  // lets exactly one submit through per accepted click
  var startedAt = 0;
  var ceilingTimer = null;
  var graceTimer = null;

  function cartIsVisible() {
    for (var i = 0; i < VISIBLE_CLASSES.length; i++) {
      if (document.body.classList.contains(VISIBLE_CLASSES[i])) return true;
    }
    return false;
  }

  function startLoading(btn) {
    if (!btn) return;
    activeBtn = btn;
    submitPass = btn;
    startedAt = Date.now();
    btn.classList.remove(ADDED_CLASS);
    btn.classList.add(LOADING_CLASS);
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('aria-busy', 'true');
    clearTimeout(ceilingTimer);
    ceilingTimer = setTimeout(onCeiling, HARD_CEILING_MS);
  }

  // opts.immediate skips the anti-flash hold.
  function stopLoading(btn, opts) {
    btn = btn || activeBtn;
    if (!btn) return;
    var elapsed = Date.now() - startedAt;
    if (!(opts && opts.immediate) && elapsed < MIN_VISIBLE_MS) {
      setTimeout(function () { stopLoading(btn, { immediate: true }); }, MIN_VISIBLE_MS - elapsed);
      return;
    }
    clearTimeout(ceilingTimer);
    clearTimeout(graceTimer);
    btn.classList.remove(LOADING_CLASS);
    btn.removeAttribute('aria-disabled');
    btn.removeAttribute('aria-busy');
    if (activeBtn === btn) { activeBtn = null; submitPass = null; }
  }

  // Only called on a path where we OBSERVED rebuy:cart.change — i.e. the add
  // really landed but nothing revealed the cart. Never called on the blind
  // ceiling path, because there the add may well have failed (sold-out variant),
  // and claiming "Added" then would be a lie.
  function confirmAndRestore(btn) {
    btn = btn || activeBtn;
    if (!btn) return;
    stopLoading(btn, { immediate: true });
    btn.setAttribute('data-rebuy-atc-added', CFG.addedLabel || 'Added ✓');
    btn.classList.add(ADDED_CLASS);
    setTimeout(function () {
      btn.classList.remove(ADDED_CLASS);
      btn.removeAttribute('data-rebuy-atc-added');
    }, ADDED_HOLD_MS);
  }

  // Hard ceiling: restore SILENTLY. We do not know whether the add succeeded.
  function onCeiling() {
    if (!activeBtn) return;
    stopLoading(activeBtn, { immediate: true });
  }

  /* ---------- click: mark state, block re-entry, never block the first add ---------- */

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest(ATC_SELECTOR);
    if (!btn) return;

    // The theme sets the `disabled` attribute (and `.disabled`) on
    // `.CustombtnDisabled` for sold-out swatch/size combinations — theme.js
    // @172311 / @183667. Respect it; never fight it.
    if (btn.disabled || btn.classList.contains('disabled')) return;
    if (btn.classList.contains('disable-rebuy') || btn.hasAttribute('disable-rebuy')) return;

    // `body.focus-enabled` makes the theme's ajaxAddListner early-return, so the
    // form submits natively and the page navigates. No Smart Cart, no spinner
    // needed — and we must not interfere with that path.
    if (document.body.classList.contains('focus-enabled')) return;

    // An add is already in flight (this button or any other ATC on the page).
    // Swallow the click so the theme's jQuery handler cannot fire a second POST.
    if (activeBtn) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    startLoading(btn); // deliberately NO preventDefault — the theme's add must run
  }, true);

  /* ---------- submit: cover the Enter-key path without cancelling our own click ---------- */

  document.addEventListener('submit', function (e) {
    var form = e.target && e.target.closest && e.target.closest(FORM_SELECTOR);
    if (!form) return;
    var btn = form.querySelector(ATC_SELECTOR);

    if (submitPass && submitPass === btn) { submitPass = null; return; } // the click we accepted
    if (activeBtn) { e.preventDefault(); e.stopImmediatePropagation(); return; }
    if (btn) { startLoading(btn); submitPass = null; }
  }, true);

  /* ---------- clear signals ---------- */

  // Primary: the visible-state body class. Preferred over rebuy:smartcart.show
  // because that event fires ~90-120ms before the flyout paints (measured).
  //
  // The class itself still lands a few ms BEFORE the drawer's first painted frame
  // (measured: cleared 318ms vs first paint 327ms), so hold for REVEAL_SETTLE_MS and
  // a frame to guarantee the spinner never disappears while the screen is still blank.
  var REVEAL_SETTLE_MS = CFG.revealSettleMs || 80;

  function stopLoadingWhenPainted() {
    requestAnimationFrame(function () {
      setTimeout(function () { stopLoading(); }, REVEAL_SETTLE_MS);
    });
  }

  function watchBody() {
    if (!document.body) { return void requestAnimationFrame(watchBody); }
    new MutationObserver(function () {
      if (!activeBtn) return;
      if (cartIsVisible()) stopLoadingWhenPainted();
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }
  watchBody();

  // Backstop: if the visible class is ever renamed, the documented show event
  // still clears the spinner (a ~100ms early clear beats a stuck button).
  document.addEventListener('rebuy:smartcart.show', function () {
    if (!activeBtn) return;
    setTimeout(function () { stopLoading(); }, 150 + REVEAL_SETTLE_MS);
  });

  // Escalation: the cart DID change but nothing revealed it. Ask the bridge to
  // reveal; if that also fails, confirm the add so the shopper isn't left
  // wondering whether it worked.
  document.addEventListener('rebuy:cart.change', function () {
    if (!activeBtn) return;
    clearTimeout(graceTimer);
    graceTimer = setTimeout(function () {
      if (!activeBtn) return;
      var bridge = window.RebuyCartBridge;
      if (bridge && typeof bridge.reveal === 'function') bridge.reveal();
      setTimeout(function () {
        if (activeBtn) confirmAndRestore(activeBtn);
      }, CHANGE_GRACE_MS);
    }, CHANGE_GRACE_MS);
  });

  // The bridge exhausted its bounded wait and there is nowhere to send the
  // shopper (no native drawer, /cart disabled). Confirm on the button so the
  // click is never silently dead.
  document.addEventListener('rebuy-bridge:fallback', function () {
    if (activeBtn) confirmAndRestore(activeBtn);
  });

  // The theme's own post-add event (dispatched by updateHeaderTotal after its
  // follow-up fetch('/cart.js')). Useful as an "add definitely landed" signal.
  document.addEventListener('theme:cart:change', function () {
    if (!activeBtn) return;
    clearTimeout(graceTimer);
    graceTimer = setTimeout(function () {
      if (activeBtn && !cartIsVisible()) confirmAndRestore(activeBtn);
    }, CHANGE_GRACE_MS);
  });
})();
